import { create } from 'zustand';
import { Session, User, AuthChangeEvent } from '@supabase/supabase-js';
import { supabase } from '../../infrastructure/supabase/supabaseClient';

interface AuthState {
  session: Session | null;
  user: User | null;
  isClinico: boolean;
  isSecretaria: boolean;
  workspaceIds: string[];
  isInitialized: boolean;
  isFetchingFallback: boolean;
  setSession: (session: Session | null) => Promise<void>;
  initialize: () => Promise<void>;
  clearSession: () => void;
}

export const useAuthStore = create<AuthState>((set, get) => ({
  session: null,
  user: null,
  isClinico: false,
  isSecretaria: false,
  workspaceIds: [],
  isInitialized: false,
  isFetchingFallback: false,

  setSession: async (session) => {
    let isClinico = session?.user?.app_metadata?.is_clinico === true;
    let isSecretaria = session?.user?.app_metadata?.is_secretaria === true;
    let workspaceIds = session?.user?.app_metadata?.workspace_ids || [];

    if (session && session.user.app_metadata?.is_clinico === undefined) {
      if (get().user?.id === session.user.id && (get().isClinico || get().isSecretaria)) {
        isClinico = get().isClinico;
        isSecretaria = get().isSecretaria;
        workspaceIds = get().workspaceIds;
      } else if ((get() as any).fallbackPromise) {
        await (get() as any).fallbackPromise;
        // After the promise resolves, we should re-check the state
        isClinico = get().isClinico;
        isSecretaria = get().isSecretaria;
        workspaceIds = get().workspaceIds;
      } else {
        const promise = (async () => {
          try {
            set({ isFetchingFallback: true });
            console.log("[useAuthStore] Claims no encontrados en JWT. Ejecutando RPC Fallback...");
            const { data: fallbackData, error } = await supabase.rpc('get_auth_roles_fallback');
            
            if (!error && fallbackData) {
              isClinico = fallbackData.is_clinico === true;
              isSecretaria = fallbackData.is_secretaria === true;
              workspaceIds = fallbackData.workspace_ids || [];
              
              if (session.user.app_metadata) {
                 session.user.app_metadata.is_clinico = isClinico;
                 session.user.app_metadata.is_secretaria = isSecretaria;
                 session.user.app_metadata.workspace_ids = workspaceIds;
              }
            }
          } catch (err) {
            console.error("[useAuthStore] Fallback RPC lanzó una excepción:", err);
          } finally {
            set({ isFetchingFallback: false });
          }
        })();
        set({ fallbackPromise: promise } as any);
        await promise;
        set({ fallbackPromise: null } as any);
      }
    }
    
    // Solo actualizamos el store UNA vez
    set({ session, user: session?.user || null, isClinico, isSecretaria, workspaceIds });
  },

  clearSession: () => set({ session: null, user: null, isClinico: false, isSecretaria: false, workspaceIds: [] }),

  initialize: async () => {
    // 1. Obtener la sesión inicial
    const { data: { session } } = await supabase.auth.getSession();
    
    // Función auxiliar para procesar los roles asíncronamente
    const processSessionRoles = async (currentSession: Session | null) => {
      if (!currentSession) {
        set({ session: null, user: null, isClinico: false, isSecretaria: false, workspaceIds: [], isInitialized: true });
        return;
      }

      let isClinico = currentSession.user.app_metadata?.is_clinico === true;
      let isSecretaria = currentSession.user.app_metadata?.is_secretaria === true;
      let workspaceIds = currentSession.user.app_metadata?.workspace_ids || [];

      // FALLBACK DE EMERGENCIA: Si Supabase Auth falla y no inyecta los claims, los buscamos manualmente.
      // Sabemos que faltan si is_clinico es undefined (ya que el hook devuelve false explícitamente cuando no lo es).
      if (currentSession.user.app_metadata?.is_clinico === undefined) {
        console.log("[useAuthStore] Claims no encontrados en JWT. Ejecutando RPC Fallback...");
        const { data: fallbackData, error } = await supabase.rpc('get_auth_roles_fallback');
        if (!error && fallbackData) {
          isClinico = fallbackData.is_clinico === true;
          isSecretaria = fallbackData.is_secretaria === true;
          workspaceIds = fallbackData.workspace_ids || [];
          
          // Inyectamos los claims obtenidos en el app_metadata local para que otras partes del código que lo lean funcionen
          if (currentSession.user.app_metadata) {
             currentSession.user.app_metadata.is_clinico = isClinico;
             currentSession.user.app_metadata.is_secretaria = isSecretaria;
             currentSession.user.app_metadata.workspace_ids = workspaceIds;
          }
        }
      }

      set({ session: currentSession, user: currentSession.user, isClinico, isSecretaria, workspaceIds, isInitialized: true });
    };

    await processSessionRoles(session);

    // 2. Suscribirse a cambios de estado de autenticación
    supabase.auth.onAuthStateChange(async (_event: AuthChangeEvent, newSession: Session | null) => {
      await processSessionRoles(newSession);
    });
  },
}));
